import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { XCircle, Eye, AlertCircle, ChevronRight, FileText, CheckSquare, Code2 } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../supabase/client';

interface WrongGroup {
  organization_code: string;
  category_code: string;
  level_text: string;
  organization_name: string;
  category_name: string;
  level_name: string;
  count: number;
  type_stats: string;
  is_invalid: boolean;
}

export default function WrongQuestionsPage() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<WrongGroup[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      navigate('/login', { replace: true });
      return;
    }
    setUser(session.user);
    await loadGroups(session.access_token);
    setLoading(false);
  };

  const loadGroups = async (accessToken: string) => {
    setLoading(true);
    try {
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/user-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ action: 'get_wrong_groups' })
      });

      const result = await response.json();
      if (result.success) {
        setGroups(result.groups || []);
        setTotalCount(result.total_count || 0);
      }
    } catch (error) {
      // 静默处理
    }
    setLoading(false);
  };

  const handleViewGroup = (group: WrongGroup) => {
    navigate(`/wrong-questions/review?org=${encodeURIComponent(group.organization_code)}&cat=${encodeURIComponent(group.category_code)}&level=${encodeURIComponent(group.level_text)}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-3 border-rose-600 border-t-transparent"></div>
          <p className="text-gray-500 text-sm">加载错题...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* 页面标题 */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-rose-500 to-pink-600 rounded-xl shadow-lg">
                  <XCircle className="w-6 h-6 text-white" />
                </div>
                我的错题
              </h1>
              <p className="text-gray-500 mt-2 ml-1">共 <span className="font-semibold text-rose-600">{totalCount}</span> 道错题待复习</p>
            </div>
            <button
              onClick={() => navigate('/assignments')}
              className="px-5 py-2.5 bg-white text-gray-700 rounded-xl hover:bg-gray-50 transition-all shadow-sm border border-gray-200 font-medium"
            >
              返回我的作业
            </button>
          </div>
        </div>

        {groups.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-16 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckSquare className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">太棒了！暂无错题</h3>
            <p className="text-gray-500 mb-6">继续保持，加油刷题</p>
            <button
              onClick={() => navigate('/exam-categories')}
              className="px-8 py-3 bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-xl hover:from-rose-600 hover:to-pink-700 transition-all shadow-md font-medium"
            >
              去刷题
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((group, index) => (
              <motion.div
                key={`${group.organization_code}-${group.category_code}-${group.level_text}`}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => !group.is_invalid && handleViewGroup(group)}
                className={`group relative rounded-2xl shadow-sm border-2 p-5 cursor-pointer transition-all duration-200 ${
                  group.is_invalid
                    ? 'bg-gray-50 border-gray-200 opacity-60'
                    : 'bg-white border-transparent hover:border-rose-300 hover:shadow-lg hover:-translate-y-0.5'
                }`}
              >
                <div className="flex items-center justify-between">
                  {/* 左侧：分类信息 + 题型统计 */}
                  <div className="flex-1 min-w-0">
                    {/* 考评体系 - 课程分类 - 级别 */}
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${
                        group.is_invalid 
                          ? 'bg-gray-200 text-gray-600' 
                          : 'bg-gradient-to-r from-rose-500 to-pink-600 text-white shadow-sm'
                      }`}>
                        {group.organization_name}
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                      <span className="text-lg font-bold text-gray-800">{group.category_name}</span>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                      <span className="text-base font-medium text-gray-600">{group.level_name}</span>
                    </div>

                    {/* 题型统计标签 */}
                    {group.type_stats && !group.is_invalid && (
                      <div className="flex flex-wrap gap-2">
                        {group.type_stats.split('  ').map((stat, idx) => {
                          const match = stat.match(/^(.+?)(\d+)$/);
                          const typeName = match ? match[1] : stat;
                          const count = match ? match[2] : '';
                          
                          let icon = <FileText className="w-3.5 h-3.5" />;
                          let bgColor = 'bg-blue-50 text-blue-700 border-blue-200';
                          
                          if (typeName.includes('单选')) {
                            icon = <CheckSquare className="w-3.5 h-3.5" />;
                            bgColor = 'bg-emerald-50 text-emerald-700 border-emerald-200';
                          } else if (typeName.includes('多选')) {
                            icon = <CheckSquare className="w-3.5 h-3.5" />;
                            bgColor = 'bg-cyan-50 text-cyan-700 border-cyan-200';
                          } else if (typeName.includes('判断')) {
                            icon = <CheckSquare className="w-3.5 h-3.5" />;
                            bgColor = 'bg-amber-50 text-amber-700 border-amber-200';
                          } else if (typeName.includes('编程') || typeName.includes('图形化')) {
                            icon = <Code2 className="w-3.5 h-3.5" />;
                            bgColor = 'bg-purple-50 text-purple-700 border-purple-200';
                          }
                          
                          return (
                            <span 
                              key={idx}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border ${bgColor}`}
                            >
                              {icon}
                              {typeName}{count}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    
                    {group.is_invalid && (
                      <div className="flex items-center gap-2 text-gray-500">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-sm">该试卷已失效</span>
                      </div>
                    )}
                  </div>

                  {/* 右侧：查看按钮 */}
                  {!group.is_invalid && (
                    <div className="ml-6 shrink-0">
                      <div className="flex items-center gap-2 text-rose-600 group-hover:text-rose-700 transition-colors">
                        <span className="text-sm font-medium">查看详情</span>
                        <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}